import { useState } from "react";
import { Plug, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PROVIDERS, listExternalModels } from "../../lib/externalLlm";
import { CmdLine, detectOS } from "../InstallCommands";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

function setupSteps(provider, os, origin) {
  if (provider === "lmstudio") {
    return [
      { t: "Ouvrez LM Studio et téléchargez un modèle (onglet Découvrir).", link: ["Télécharger LM Studio", "https://lmstudio.ai/download"] },
      { t: "Démarrez le serveur local avec CORS activé (ou onglet Développeur › Start Server, puis option « Enable CORS »).", cmd: "lms server start --cors" },
      { t: "Revenez ici et cliquez sur « Se connecter »." },
    ];
  }
  const origins = {
    windows: `setx OLLAMA_ORIGINS "${origin}"`,
    mac: `launchctl setenv OLLAMA_ORIGINS "${origin}"`,
    linux: `OLLAMA_ORIGINS="${origin}" ollama serve`,
  };
  return [
    { t: "Installez Ollama et téléchargez un modèle.", link: ["Télécharger Ollama", "https://ollama.com/download"], cmd: "ollama pull qwen2.5:7b" },
    { t: "Autorisez ce site à parler à Ollama (une seule fois), puis quittez et relancez Ollama.", cmd: origins[os] },
    { t: "Revenez ici et cliquez sur « Se connecter ». Si le navigateur demande l'accès au réseau local, acceptez." },
  ];
}

export function ExternalLlmPanel({ ext, setExt }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [os, setOs] = useState(detectOS);
  const steps = setupSteps(ext.provider, os, window.location.origin);

  const connect = async () => {
    setBusy(true);
    setError("");
    try {
      const models = await listExternalModels(ext.base);
      if (!models.length) throw new Error("aucun modèle installé");
      setExt({ ...ext, models, model: models.includes(ext.model) ? ext.model : models[0], connected: true });
      toast.success(`Connecté à ${PROVIDERS[ext.provider].name}`);
    } catch (e) {
      setExt({ ...ext, connected: false });
      setError(e.message === "aucun modèle installé" ? "Connecté, mais aucun modèle n'est installé." : "Connexion impossible. Vérifiez que l'outil est lancé et que l'étape 2 est faite.");
    }
    setBusy(false);
  };

  const pickProvider = (p) => setExt({ provider: p, base: PROVIDERS[p].base, models: [], model: "", connected: false });

  return (
    <div className="space-y-4 rounded-lg border border-[#1E2638] bg-[#080B10] p-4" data-testid="external-llm-panel">
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(PROVIDERS).map(([id, p]) => (
          <button key={id} onClick={() => pickProvider(id)} data-testid={`external-provider-${id}`}
            className={`rounded-md border px-3 py-1.5 text-xs transition-colors ${ext.provider === id ? "border-primary bg-primary/10 text-primary" : "border-[#1E2638] text-muted-foreground hover:text-slate-200"}`}>
            {p.name}
          </button>
        ))}
        <Input value={ext.base} onChange={(e) => setExt({ ...ext, base: e.target.value, connected: false })} data-testid="external-base-url" className="h-8 w-[220px] font-mono2 text-xs" />
        <Button onClick={connect} disabled={busy} size="sm" data-testid="external-connect-button" className="gap-1.5">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plug className="h-3.5 w-3.5" />} Se connecter
        </Button>
        {ext.connected && <span data-testid="external-connected" className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-4 w-4" /> Connecté</span>}
      </div>

      {ext.connected && (
        <Select value={ext.model} onValueChange={(m) => setExt({ ...ext, model: m })}>
          <SelectTrigger data-testid="external-model-select" className="w-full sm:w-[320px]"><SelectValue /></SelectTrigger>
          <SelectContent>{ext.models.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {error && <p data-testid="external-error" className="flex items-center gap-1.5 text-xs text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> {error}</p>}

      {!ext.connected && (
        <div className="space-y-3 border-t border-[#1E2638] pt-3" data-testid="external-setup-guide">
          <div className="flex items-center gap-1.5">
            <span className="mr-1 text-[11px] uppercase tracking-wide text-muted-foreground">Système</span>
            {[["windows", "Windows"], ["mac", "macOS"], ["linux", "Linux"]].map(([id, l]) => (
              <button key={id} onClick={() => setOs(id)} data-testid={`external-os-${id}`}
                className={`rounded border px-2 py-0.5 text-[11px] ${os === id ? "border-primary text-primary" : "border-[#1E2638] text-muted-foreground"}`}>{l}</button>
            ))}
          </div>
          {steps.map((s, i) => (
            <div key={s.t} className="flex gap-3">
              <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-primary/60 font-mono2 text-[10px] text-primary">{i + 1}</div>
              <div className="min-w-0 flex-1 space-y-1.5 text-xs text-slate-300">
                <p>{s.t} {s.link && <a href={s.link[1]} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">{s.link[0]}</a>}</p>
                {s.cmd && <CmdLine cmd={s.cmd} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
